import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cartAPI } from '../services/api';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

const EMPTY_CART = { items: [], subtotal: 0, totalDeposit: 0, grandTotal: 0, itemCount: 0 };

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart]     = useState(EMPTY_CART);
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!user) { setCart(EMPTY_CART); return; }
    try {
      setLoading(true);
      const { data } = await cartAPI.get();
      setCart(data.cart);
    } catch (e) { console.error('Cart fetch error:', e); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  // Reset cart on logout
  useEffect(() => {
    if (!user) setCart(EMPTY_CART);
  }, [user]);

  const addToCart = useCallback(async (itemData) => {
    const { data } = await cartAPI.add(itemData);
    setCart(data.cart);
    return data;
  }, []);

  const removeFromCart = useCallback(async (itemId) => {
    const { data } = await cartAPI.remove(itemId);
    // Backend now returns updated cart
    if (data.cart) setCart(data.cart);
    else await fetchCart();
  }, [fetchCart]);

  const updateCartItem = useCallback(async (itemId, updates) => {
    const { data } = await cartAPI.update(itemId, updates);
    setCart(data.cart);
    return data;
  }, []);

  // clearCart() — calls the backend DELETE then resets local state.
  // Use this when YOU are clearing the cart (e.g. a "clear all" button).
  const clearCart = useCallback(() => {
    // Synchronously reset local state — the backend already cleared the cart
    // (COD and Stripe webhook both do it). No need for a round-trip.
    setCart(EMPTY_CART);
  }, []);

  // clearCartRemote() — clears both backend and local state.
  // Use this for explicit user-initiated "clear cart" actions.
  const clearCartRemote = useCallback(async () => {
    await cartAPI.clear();
    setCart(EMPTY_CART);
  }, []);

  return (
    <CartContext.Provider value={{ cart, loading, fetchCart, addToCart, removeFromCart, updateCartItem, clearCart, clearCartRemote }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be inside CartProvider');
  return ctx;
};
